import React, { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SideNavigation, {
  SideNavigationProps,
} from "@amzn/awsui-components-react/polaris/side-navigation";
import {
  DEFAULT_LEFT_NAV_PATH,
  LEFT_NAV_ITEMS,
  LeftNavPath,
  resolveLeftNavPath,
} from "./shellRoutes";
import { getSearchForNavigation, TAB_ID_KEY } from "../../util/urlUtils";

const VALIDATION_PATH: LeftNavPath = "/validation";

/** App Shell left nav. Items follow `LEFT_NAV_ITEMS` order. */

const HEADER_TEXT = "Devices ASIN Central";

export const LeftNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const activePath = useMemo<LeftNavPath>(
    () => resolveLeftNavPath(location.pathname),
    [location.pathname]
  );

  const items = useMemo<ReadonlyArray<SideNavigationProps.Item>>(() => {
    // Group the primary nav items with a divider before Date Baseline
    // so Date Baseline and the Stamper Tool read as distinct utilities
    // separate from the ASIN pipeline pages above them.
    const UTILITY_GROUP_START_PATH: LeftNavPath = "/baseline";
    const primary: SideNavigationProps.Item[] = [];
    LEFT_NAV_ITEMS.forEach((item) => {
      if (item.path === UTILITY_GROUP_START_PATH) {
        primary.push({ type: "divider" });
      }
      primary.push({
        type: "link",
        text: item.label,
        href: item.path,
      });
    });

    return primary;
  }, []);

  const handleFollow = useCallback(
    (event: CustomEvent<SideNavigationProps.FollowDetail>) => {
      // External links open in a new tab natively.
      if (event.detail.external) return;
      // Nav without page reload. Preserve `hash` so the Applied
      // Filter (encoded in the hash) survives across routes.
      // Strip `tabId` when leaving `/validation` since it's a
      // Validation-only param and would be ignored elsewhere.
      event.preventDefault();
      const isValidationDestination = event.detail.href === VALIDATION_PATH;
      const search = getSearchForNavigation(
        isValidationDestination ? [] : [TAB_ID_KEY]
      );
      navigate({
        pathname: event.detail.href,
        search,
        hash: window.location.hash,
      });
    },
    [navigate]
  );

  return (
    <SideNavigation
      header={{ text: HEADER_TEXT, href: DEFAULT_LEFT_NAV_PATH }}
      items={items}
      activeHref={activePath}
      onFollow={handleFollow}
    />
  );
};

// Exported for unit tests.
export const LEFT_NAV_HEADER_TEXT = HEADER_TEXT;

export default LeftNav;
